import React, { useState } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import {
  Box,
  FormControl,
  FormHelperText,
  Grid,
  Typography,
  Autocomplete,
} from "@mui/material";
import { StyledLoadingButton } from "../../../customComponents/styled/styledButtons";
import {
  StyledTextField,
} from "../../../customComponents/styled/styledInputs";
import { useGetSubSectionsPaginatedQuery } from "../sections/sectionsApiSlice";
import { useUpdateTraitMutation, useGetClassroomsPaginatedQuery } from "./traitsApiSlice";
import toast from "../../../../helpers/toast";

function EditTraitCategoryForm({ item, setItem }) {
  const [subsectionSearch, setSubsectionSearch] = useState("");
  const [classroomSearch, setClassroomSearch] = useState("");
  const { data: subsectionsData } = useGetSubSectionsPaginatedQuery({ page: 1, per_page: 10, q: subsectionSearch });
  const subsections = subsectionsData?.subsections ?? [];
  const { data: classroomsData } = useGetClassroomsPaginatedQuery({ page: 1, per_page: 10, q: classroomSearch });
  const classrooms = classroomsData?.classrooms ?? [];
  const [updateTrait, { isLoading }] = useUpdateTraitMutation();

  const createTraitForm = useFormik({
    initialValues: {
      id: item.id,
      name: item.name,
      subsection_id: item.classroom_id ? "" : (item.subsection_id || ""),
      classroom_id: item.classroom_id || "",
      color: item.color || "#000000",
    },
    enableReinitialize: true,
    validationSchema: yup.object({
      name: yup.string().required("Required"),
    }).test(
      "subsection-or-classroom",
      "Either a subsection or a classroom is required",
      function (values) {
        if (!values.subsection_id && !values.classroom_id) {
          return this.createError({
            path: "subsection_id",
            message: "Either subsection or classroom is required",
          });
        }
        return true;
      }
    ),
    onSubmit: async (values) => {
      try {
        const payload = {
          id: values.id,
          name: values.name,
          color: values.color,
          ...(values.classroom_id
            ? { classroom_id: values.classroom_id, subsection_id: null }
            : { subsection_id: values.subsection_id, classroom_id: null }),
        };
        const response = await updateTrait(payload).unwrap();
        if (response.id) {
          toast.success("Trait updated successfully");
          createTraitForm.resetForm();
          setItem(null);
        } else {
          toast.error("Trait update failed");
        }
      } catch (error) {
        toast.error(error?.data?.message || error?.message || "Something went wrong");
      }
    },
  });
  return (
    <>
      <Box className="paperHeader">
        <Typography variant="formHeading">Edit Trait Category</Typography>
      </Box>
      <Box className="paperBody">
        <form onSubmit={createTraitForm.handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs>
              <FormControl error fullWidth>
                <Typography variant="formSubHeading">Trait</Typography>
                <StyledTextField
                  autoFocus
                  variant="outlined"
                  id="name"
                  name="name"
                  value={createTraitForm.values.name}
                  onChange={createTraitForm.handleChange}
                  error={
                    createTraitForm.touched.name &&
                    Boolean(createTraitForm.errors.name)
                  }
                />
                <FormHelperText variant="error">
                  {createTraitForm.touched.name && createTraitForm.errors.name}
                </FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs="auto">
              <FormControl>
                <Typography variant="formSubHeading">Color</Typography>
                <input
                  type="color"
                  id="color"
                  name="color"
                  value={createTraitForm.values.color}
                  onChange={createTraitForm.handleChange}
                  style={{ width: "48px", height: "40px", cursor: "pointer", border: "none", padding: 0, background: "none" }}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <Typography variant="formSubHeading">Subsection</Typography>
                <Autocomplete
                  disabled={Boolean(createTraitForm.values.classroom_id)}
                  options={subsections}
                  getOptionLabel={(option) => option.name || ""}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  value={subsections.find(s => s.id === createTraitForm.values.subsection_id) || null}
                  onChange={(_, value) => {
                    createTraitForm.setFieldValue("subsection_id", value?.id || "");
                    if (value) createTraitForm.setFieldValue("classroom_id", "");
                  }}
                  onInputChange={(_, value, reason) => { if (reason === "input") setSubsectionSearch(value); }}
                  filterOptions={(x) => x}
                  renderInput={(params) => (
                    <StyledTextField
                      {...params}
                      placeholder={createTraitForm.values.classroom_id ? "Disabled — classroom selected" : "Search subsection..."}
                      error={createTraitForm.touched.subsection_id && Boolean(createTraitForm.errors.subsection_id)}
                    />
                  )}
                />
                <FormHelperText variant="error">
                  {createTraitForm.touched.subsection_id && createTraitForm.errors.subsection_id}
                </FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <Typography variant="formSubHeading">
                  Classroom{" "}
                  <Typography component="span" variant="caption" color="text.secondary">
                    (optional — overrides subsection traits for this class)
                  </Typography>
                </Typography>
                <Autocomplete
                  disabled={Boolean(createTraitForm.values.subsection_id)}
                  options={classrooms}
                  getOptionLabel={(option) => option.name || ""}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  value={classrooms.find(c => c.id === createTraitForm.values.classroom_id) || null}
                  onChange={(_, value) => {
                    createTraitForm.setFieldValue("classroom_id", value?.id || "");
                    if (value) createTraitForm.setFieldValue("subsection_id", "");
                  }}
                  onInputChange={(_, value, reason) => { if (reason === "input") setClassroomSearch(value); }}
                  filterOptions={(x) => x}
                  renderInput={(params) => (
                    <StyledTextField
                      {...params}
                      placeholder={createTraitForm.values.subsection_id ? "Disabled — subsection selected" : "Search classroom..."}
                    />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} textAlign={"right"}>
              <StyledLoadingButton loading={isLoading} type="submit">
                Submit
              </StyledLoadingButton>
            </Grid>
          </Grid>
        </form>
      </Box>
    </>
  );
}

export default EditTraitCategoryForm;
